/**
 * /dashboard/posh/policy/$id — Policy detail.
 *
 * GET /posh/policy/policies/{id} → policy + immutable version snapshots.
 * NOT 2FA-gated. Read-only for everyone; edit/publish controls gated on
 * usePoshAdmin AND policy status.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/policy/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { poshApi, toApiError } from "@/lib/api";
import { usePolicy, usePublishPolicy } from "@/hooks/use-posh";
import { fmtDate, sectionsOf } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { PolicyEditor } from "@/components/posh/policy-editor";
import { usePoshAdmin } from "@/components/posh/use-posh-admin";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/policy/$id",
)({
  component: PolicyDetailRoute,
});

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function PolicyDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const q = usePolicy(numId);
  const isAdmin = usePoshAdmin();
  const publishMut = usePublishPolicy(numId);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  async function onDownload(versionId?: number) {
    const key = versionId ? `v${versionId}` : "live";
    setPdfBusy(key);
    try {
      const blob = await poshApi.downloadPolicyPdf(numId, versionId);
      downloadBlob(
        blob,
        `workright-posh-policy-${numId}${versionId ? `-v${versionId}` : ""}.pdf`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setPdfBusy(null);
    }
  }

  async function onPublish() {
    try {
      await publishMut.mutateAsync(undefined);
      toast.success("Policy published. This version is now frozen.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <PoshModuleShell
      title="POSH Policy"
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/policy" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {q.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this policy</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data &&
        (() => {
          const { policy, versions } = q.data;
          const isDraft = policy.status === "draft";
          const sections = sectionsOf(policy.content);
          return (
            <div className="space-y-5">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        {policy.title}
                      </CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Version {policy.version} · Updated{" "}
                        {fmtDate(policy.updated_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={policy.status} />
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        disabled={pdfBusy === "live"}
                        onClick={() => onDownload()}
                      >
                        {pdfBusy === "live" ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        PDF
                      </Button>
                      {isAdmin && isDraft && (
                        <Button
                          size="sm"
                          className="gap-1.5"
                          disabled={publishMut.isPending}
                          onClick={onPublish}
                        >
                          {publishMut.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          Publish
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {sections.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      This policy has no content sections yet.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {sections.map((s, i) => (
                        <div key={`${i}-${s.heading ?? "section"}`}>
                          <h3 className="text-sm font-semibold text-gray-900">
                            {s.heading || `Section ${i + 1}`}
                          </h3>
                          <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                            {s.body}
                          </p>
                          {i < sections.length - 1 && (
                            <Separator className="mt-4" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {isAdmin && isDraft && <PolicyEditor policy={policy} />}
              {isAdmin && !isDraft && (
                <Alert>
                  <AlertTitle>This policy is {policy.status}</AlertTitle>
                  <AlertDescription>
                    Published versions are frozen and cannot be edited.
                    Generate a new policy to make changes.
                  </AlertDescription>
                </Alert>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Version history
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {versions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No published versions yet. Publishing freezes the
                      current content as an immutable version.
                    </p>
                  ) : (
                    <ul className="divide-y">
                      {[...versions]
                        .sort((a, b) => b.version - a.version)
                        .map((v) => (
                          <li
                            key={v.id}
                            className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Version {v.version}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Frozen {fmtDate(v.created_at)}
                                {v.change_summary
                                  ? ` · ${v.change_summary}`
                                  : ""}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1.5"
                              disabled={pdfBusy === `v${v.id}`}
                              onClick={() => onDownload(v.id)}
                            >
                              {pdfBusy === `v${v.id}` ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                              PDF
                            </Button>
                          </li>
                        ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })()}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
